import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, CardBody, Nav, NavItem, NavLink, TabContent, TabPane, Button } from "reactstrap";
import classnames from "classnames";
import api from "../../api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import PvPChallengeReceivedModal from "../../components/Modals/PvPChallengeReceivedModal";
import PvPChallengeModal from "../../components/Modals/PvPChallengeModal";
import "./ManageBattles.css";

const ManageBattles = () => {
  document.title = "Pokemon Radiance | Manage Battles";
  
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("find");
  const [receivedChallenges, setReceivedChallenges] = useState([]);
  const [sentChallenges, setSentChallenges] = useState([]);
  const [activeBattles, setActiveBattles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  
  // Find Players tab state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // Fetch challenges and battles
  const fetchData = async () => {
    setLoading(true);
    try {
      const [challengesRes, battlesRes] = await Promise.all([
        api.get("/v1/pvp/challenges/pending", { withCredentials: true }),
        api.get("/v1/pvp/battles/active", { withCredentials: true })
      ]);

      setReceivedChallenges(challengesRes.data.received || []);
      setSentChallenges(challengesRes.data.sent || []);
      setActiveBattles(battlesRes.data || []);
    } catch (error) {
      console.error("Failed to fetch battle data:", error);
      toast.error("Failed to load battle data", {
        position: "top-center",
        autoClose: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    
    // Refresh every 10 seconds
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  // View challenge details - data is already loaded from pending challenges
  const handleViewDetails = (challenge) => {
    setSelectedChallenge(challenge);
  };

  // Accept challenge from modal
  const handleAcceptFromModal = async (response) => {
    toast.success("Challenge accepted! Starting battle...", {
      position: "top-center",
      autoClose: 2000
    });

    // Navigate to battle screen
    setTimeout(() => {
      navigate(`/pvp-battle/${response.battle_id}`);
    }, 2000);
    
    fetchData();
  };

  // Decline challenge from modal
  const handleDeclineFromModal = () => {
    toast.info("Challenge declined", {
      position: "top-center",
      autoClose: 2000
    });
    
    fetchData();
  };

  // Decline challenge
  const handleDecline = async (challengeId) => {
    try {
      await api.post(`/v1/pvp/challenge/${challengeId}/decline`, {}, { withCredentials: true });
      
      toast.info("Challenge declined", {
        position: "top-center",
        autoClose: 2000
      });
      
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to decline challenge";
      toast.error(errorMsg, {
        position: "top-center",
        autoClose: 3000
      });
    }
  };

  // Cancel sent challenge
  const handleCancel = async (challengeId) => {
    try {
      await api.delete(`/v1/pvp/challenge/${challengeId}`, { withCredentials: true });
      
      toast.info("Challenge cancelled", {
        position: "top-center",
        autoClose: 2000
      });
      
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to cancel challenge";
      toast.error(errorMsg, {
        position: "top-center",
        autoClose: 3000
      });
    }
  };

  // Join active battle
  const handleJoinBattle = (battleId) => {
    navigate(`/pvp-battle/${battleId}`);
  };

  // Forfeit battle
  const handleForfeit = async (battleId) => {
    if (!window.confirm("Are you sure you want to forfeit this battle?")) {
      return;
    }

    try {
      await api.post(`/v1/pvp/battle/${battleId}/forfeit`, {}, { withCredentials: true });
      
      toast.warning("Battle forfeited", {
        position: "top-center",
        autoClose: 2000
      });
      
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to forfeit battle";
      toast.error(errorMsg, {
        position: "top-center",
        autoClose: 3000
      });
    }
  };

  // Search for players with partial matching
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.warning("Please enter a username to search", {
        position: "top-center",
        autoClose: 2000
      });
      return;
    }

    setSearching(true);
    try {
      const response = await api.get(`/v1/players/search`, { 
        params: { query: searchQuery, limit: 20 },
        withCredentials: true 
      });
      
      if (response.data.length === 0) {
        toast.info("No players found matching your search", {
          position: "top-center",
          autoClose: 3000
        });
      }
      
      setSearchResults(response.data);
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Failed to search for players", {
        position: "top-center",
        autoClose: 3000
      });
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Open challenge modal for player
  const handleChallengePlayer = (player) => {
    setSelectedPlayer(player);
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="PvP Battles" breadcrumbItem="Manage Battles" />

        <Row>
          <Col lg={12}>
            <Card className="manage-battles-card">
              <CardBody>
                <div className="manage-battles-header">
                  <h4 className="card-title mb-4">Player vs Player Battles</h4>
                  <Button
                    color="primary"
                    size="sm"
                    onClick={fetchData}
                    disabled={loading}
                  >
                  Refresh
                  </Button>
                </div>

                <Nav tabs className="nav-tabs-custom">
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "find" })}
                      onClick={() => setActiveTab("find")}
                      style={{ cursor: "pointer" }}
                    >
                      <i className="mdi mdi-magnify me-1"></i>
                      Find Players
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "received" })}
                      onClick={() => setActiveTab("received")}
                      style={{ cursor: "pointer" }}
                    >
                      Received Challenges
                      {receivedChallenges.length > 0 && (
                        <span className="badge bg-danger ms-2">{receivedChallenges.length}</span>
                      )}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "sent" })}
                      onClick={() => setActiveTab("sent")}
                      style={{ cursor: "pointer" }}
                    >
                      Sent Challenges
                      {sentChallenges.length > 0 && (
                        <span className="badge bg-info ms-2">{sentChallenges.length}</span>
                      )}
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={classnames({ active: activeTab === "active" })}
                      onClick={() => setActiveTab("active")}
                      style={{ cursor: "pointer" }}
                    >
                      Active Battles
                      {activeBattles.length > 0 && (
                        <span className="badge bg-success ms-2">{activeBattles.length}</span>
                      )}
                    </NavLink>
                  </NavItem>
                </Nav>

                <TabContent activeTab={activeTab} className="p-3">
                  {/* Find Players Tab */}
                  <TabPane tabId="find">
                    <div className="find-players-section">
                      <div className="search-bar-container mb-4">
                        <div className="input-group">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter player username..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                          />
                          <Button
                            color="primary"
                            onClick={handleSearch}
                            disabled={searching}
                          >
                            {searching ? (
                              <span className="spinner-border spinner-border-sm me-1" />
                            ) : (
                              <i className="mdi mdi-magnify me-1"></i>
                            )}
                            Search
                          </Button>
                        </div>
                      </div>

                      {searchResults.length === 0 && !searching ? (
                        <div className="text-center py-5 text-muted">
                          <i className="mdi mdi-account-search" style={{ fontSize: "48px" }}></i>
                          <p className="mt-3">Search for players to challenge them to a battle!</p>
                        </div>
                      ) : searching ? (
                        <div className="text-center py-5">
                          <div className="spinner-border text-primary" role="status">
                            <span className="sr-only">Searching...</span>
                          </div>
                        </div>
                      ) : (
                        <div className="player-results">
                          {searchResults.map((player) => (
                            <Card key={player.username} className="player-card mb-3">
                              <CardBody>
                                <div className="player-card-content">
                                  <div className="player-info-section">
                                    <div className="player-avatar me-3">
                                      <img
                                        src={`/avatars/${player.avatar}.png`}
                                        alt={player.username}
                                        style={{ width: "64px", height: "64px" }}
                                        onError={(e) => {
                                          e.target.src = "/avatars/1.png";
                                        }}
                                      />
                                      {player.is_online === 1 && (
                                        <span className="online-indicator" title="Online"></span>
                                      )}
                                    </div>
                                    <div className="player-details">
                                      <h5 className="mb-1">
                                        {player.username}
                                        {player.is_online === 1 && (
                                          <span className="badge bg-success ms-2" style={{ fontSize: "0.7rem" }}>
                                            ONLINE
                                          </span>
                                        )}
                                      </h5>
                                      <div className="player-stats text-muted">
                                        <small className="d-flex align-items-center">
                                          <img 
                                            src="/Items/Coin.png" 
                                            alt="Coins" 
                                            style={{ width: "16px", height: "16px", marginRight: "4px", imageRendering: "pixelated" }}
                                            onError={(e) => e.target.style.display = 'none'}
                                          />
                                          {player.coins?.toLocaleString() || 0}
                                          <span className="mx-2">|</span>
                                          <img 
                                            src="/Items/BattleCoin.png" 
                                            alt="Battle Points" 
                                            style={{ width: "16px", height: "16px", marginRight: "4px", imageRendering: "pixelated" }}
                                            onError={(e) => e.target.style.display = 'none'}
                                          />
                                          {player.battle_points?.toLocaleString() || 0} BP
                                        </small>
                                      </div>
                                      <div className="player-record text-muted">
                                        <small>
                                          <i className="mdi mdi-sword-cross me-1"></i>
                                          {player.pvp_wins || 0}W - {player.pvp_losses || 0}L
                                          {(player.pvp_wins || 0) + (player.pvp_losses || 0) > 0 && (
                                            <span className="ms-2">
                                              ({Math.round((player.pvp_wins || 0) / ((player.pvp_wins || 0) + (player.pvp_losses || 0)) * 100)}% WR)
                                            </span>
                                          )}
                                        </small>
                                      </div>
                                    </div>
                                  </div>
                                  <Button
                                    color="primary"
                                    className="challenge-btn"
                                    onClick={() => handleChallengePlayer(player)}
                                  >
                                    <i className="mdi mdi-sword-cross me-1"></i>
                                    Challenge
                                  </Button>
                                </div>
                              </CardBody>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  </TabPane>

                  {/* Received Challenges Tab */}
                  <TabPane tabId="received">
                    {loading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                      </div>
                    ) : receivedChallenges.length === 0 ? (
                      <div className="text-center py-5 text-muted">
                        <i className="mdi mdi-sword-cross" style={{ fontSize: "48px" }}></i>
                        <p className="mt-3">No pending challenges</p>
                      </div>
                    ) : (
                      <div className="challenges-list">
                        {receivedChallenges.map((challenge) => (
                          <Card key={challenge.challenge_id} className="challenge-card">
                            <CardBody>
                              <div className="challenge-content">
                                <div className="challenge-info">
                                  <h5 className="mb-2">
                                  {challenge.challenger_username}
                                  </h5>
                                  <p className="text-muted mb-0">
                                    <small>{formatTimeAgo(challenge.created_at)}</small>
                                  </p>
                                </div>
                                <div className="challenge-actions">
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => handleViewDetails(challenge)}
                                    className="me-2"
                                  >
                                    View Details
                                  </Button>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() => handleDecline(challenge.challenge_id)}
                                  >
                                    ✗ Decline
                                  </Button>
                                </div>
                              </div>
                            </CardBody>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabPane>

                  {/* Sent Challenges Tab */}
                  <TabPane tabId="sent">
                    {loading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                      </div>
                    ) : sentChallenges.length === 0 ? (
                      <div className="text-center py-5 text-muted">
                        <i className="mdi mdi-send" style={{ fontSize: "48px" }}></i>
                        <p className="mt-3">No pending challenges sent</p>
                      </div>
                    ) : (
                      <div className="challenges-list">
                        {sentChallenges.map((challenge) => (
                          <Card key={challenge.challenge_id} className="challenge-card">
                            <CardBody>
                              <div className="challenge-content">
                                <div className="challenge-info">
                                  <h5 className="mb-2">
                                 {challenge.opponent_username}
                                  </h5>
                                  <p className="text-muted mb-0">
                                    <small>Waiting for response • {formatTimeAgo(challenge.created_at)}</small>
                                  </p>
                                </div>
                                <div className="challenge-actions">
                                  <Button
                                    color="secondary"
                                    size="sm"
                                    onClick={() => handleCancel(challenge.challenge_id)}
                                  >
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            </CardBody>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabPane>

                  {/* Active Battles Tab */}
                  <TabPane tabId="active">
                    {loading ? (
                      <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="sr-only">Loading...</span>
                        </div>
                      </div>
                    ) : activeBattles.length === 0 ? (
                      <div className="text-center py-5 text-muted">
                        <i className="mdi mdi-pokeball" style={{ fontSize: "48px" }}></i>
                        <p className="mt-3">No active battles</p>
                      </div>
                    ) : (
                      <div className="challenges-list">
                        {activeBattles.map((battle) => (
                          <Card key={battle.battle_id} className="challenge-card battle-card-active">
                            <CardBody>
                              <div className="challenge-content">
                                <div className="challenge-info">
                                  <h5 className="mb-2">
                                   vs {battle.challenger_username === localStorage.getItem("authUser") ? battle.opponent_username : battle.challenger_username}
                                  </h5>
                                  <p className="text-muted mb-0">
                                    <small>Battle started {formatTimeAgo(battle.created_at)}</small>
                                  </p>
                                </div>
                                <div className="challenge-actions">
                                  <Button
                                    color="primary"
                                    size="sm"
                                    onClick={() => handleJoinBattle(battle.battle_id)}
                                    className="me-2"
                                  >
                                     Join Battle
                                  </Button>
                                  <Button
                                    color="warning"
                                    size="sm"
                                    onClick={() => handleForfeit(battle.battle_id)}
                                  >
                                  Forfeit
                                  </Button>
                                </div>
                              </div>
                            </CardBody>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      
      {/* Challenge Details Modal */}
      {selectedChallenge && (
        <PvPChallengeReceivedModal
          challenge={selectedChallenge}
          onClose={() => setSelectedChallenge(null)}
          onAccept={handleAcceptFromModal}
          onDecline={handleDeclineFromModal}
        />
      )}

      {/* Challenge Player Modal */}
      {selectedPlayer && (
        <PvPChallengeModal
          targetUsername={selectedPlayer.username}
          onClose={() => setSelectedPlayer(null)}
          onChallengeSuccess={() => {
            setSelectedPlayer(null);
            toast.success("Challenge sent successfully!", {
              position: "top-center",
              autoClose: 2000
            });
            // Switch to sent challenges tab
            setActiveTab("sent");
            fetchData();
          }}
        />
      )}
    </div>
  );
};

export default ManageBattles;
